import { useEffect, useState, useMemo } from "react";
import { PlusIcon } from "../../assets/icons";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routes/pageRoutes";
import { useDispatch } from "react-redux";
import {
  toggleIntroductionScreenStatus,
  deleteIntroductionScreen,
  reorderIntroductionScreen,
} from "../../redux/slice/introductionScreenSlice";
import { useToast } from "../../context/TostContext";
import DeletePopup from "../../components/delete-popup";
import ConfirmModal from "../../components/withModal/ConfirmModal";
import { usePermission } from "../../hooks/usePermission";
import { TableSearch } from "../../components/form/Fields";
import CommonButton from "../../components/commonButton";
import CustomTable from "../../components/table/CustomTable";
import StatusChip from "../../components/statusChip/StatusChip";

const MODULE_SLUG = "intro_screens";

const List = ({
  data,
  isLoading,
  isInitialLoad,
  searchTerm,
  setSearchTerm,
  setCurrentPage,
  currentPage,
  handleEditBtn,
  onView,
  refresh,
  limit,
  pagination,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [openDeleteScreenModal, setOpenDeleteScreenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusItem, setStatusItem] = useState(null);
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");
  const canDelete = hasPermission(MODULE_SLUG, "delete");

  const platformOptions = useMemo(
    () => [
      { value: "all", label: "All" },
      { value: "android", label: "Android" },
      { value: "ios", label: "iOS" },
      { value: "web", label: "Web" },
    ],
    [],
  );

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // Optimistic update for positions and numeric order values
    const updatedWithOrders = reorderedItems.map((item, index) => ({
      ...item,
      screen_order: (currentPage - 1) * (limit || 10) + index + 1,
    }));
    setItems(updatedWithOrders);

    try {
      const movedItem = removed;
      const newOrder =
        (currentPage - 1) * (limit || 10) + result.destination.index + 1;
      const res = await dispatch(
        reorderIntroductionScreen({ id: movedItem.id, order: newOrder }),
      ).unwrap();
      if (res.success) {
        toast.success("Order updated successfully");
        if (refresh) refresh();
      }
    } catch (error) {
      toast.error(error.message || "Failed to reorder");
      // Revert on error
      setItems(data || []);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedId) return;

    try {
      setIsSubmitting(true);
      const result = await dispatch(
        deleteIntroductionScreen(selectedId),
      ).unwrap();

      if (result.res.success) {
        toast.success(result.res.message || "Screen deleted successfully");
        setOpenDeleteScreenModal(false);
        setSelectedId(null);
        if (refresh) refresh();
      }
    } catch (error) {
      toast.error(error.message || "Delete failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusItem) return;
    try {
      const result = await dispatch(
        toggleIntroductionScreenStatus({
          id: statusItem.id,
          is_active: !statusItem.is_active,
        }),
      ).unwrap();
      if (result.res.success) {
        toast.success(result.res.message || "Status updated");
        if (refresh) refresh();
      }
    } catch (error) {
      toast.error(error.message || "Update failed");
    } finally {
      setShowStatusModal(false);
      setStatusItem(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "image_video",
        header: "IMAGE / VIDEO",
        cell: (_, row) =>
          row?.file?.url ? (
            row.file.mimetype?.startsWith("video/") ? (
              <video
                src={row.file.url}
                style={{
                  width: "80px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "6px",
                }}
                muted
              />
            ) : (
              <img
                src={row.file.url}
                alt="Intro Screen"
                style={{
                  width: "80px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "6px",
                }}
              />
            )
          ) : (
            "-"
          ),
      },
      {
        id: "title",
        header: "TITLE",
        cell: (_, row) => row?.title || "-",
      },
      {
        id: "platform",
        header: "PLATFORM",
        cell: (_, row) =>
          platformOptions.find((p) => p.value === row?.platform)?.label || "-",
      },
      {
        id: "screen_order",
        header: "SCREEN ORDER",
        cell: (_, row) => row?.screen_order || "0",
      },
      {
        id: "status",
        header: "STATUS",
        cell: (_, row) => (
          <StatusChip
            bg={row?.is_active ? "success" : "danger"}
            pill
            title={row?.is_active ? "Active" : "Inactive"}
            className={canUpdate ? "cursor-pointer" : ""}
            onClick={(e) => {
              e.stopPropagation();
              if (canUpdate) {
                setStatusItem(row);
                setShowStatusModal(true);
              }
            }}
          />
        ),
      },
    ],
    [canUpdate, platformOptions],
  );

  const actionList = useMemo(
    () =>
      [
        {
          title: "View",
          handler: (row) => {
            onView && onView(row);
          },
        },
        canUpdate && {
          title: "Edit",
          handler: (row) => {
            handleEditBtn(row);
          },
        },
        canUpdate && {
          title: (row) => (row?.is_active ? "Deactivate" : "Activate"),
          handler: (row) => {
            setStatusItem(row);
            setShowStatusModal(true);
          },
        },
        canDelete && {
          title: "Delete",
          handler: (row) => {
            setSelectedId(row.id);
            setOpenDeleteScreenModal(true);
          },
        },
      ].filter(Boolean),
    [canUpdate, canDelete, onView, handleEditBtn],
  );

  const isTableLoading = !isInitialLoad && isLoading;

  return (
    <>
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch
          value={searchTerm}
          placeholder="Search"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        {canCreate && (
          <CommonButton
            icon={<PlusIcon />}
            variant="primary"
            title="Add Intro Screen"
            onClick={(e) => {
              e.stopPropagation();
              navigate(routes.addIntroductionScreen);
            }}
          />
        )}
      </div>

      <CustomTable
        data={items}
        columns={columns}
        loading={isTableLoading}
        keyField="id"
        emptyMessage="No introduction screens found"
        actionList={actionList}
        isDragable={canUpdate}
        onDragEnd={handleDragEnd}
        pagination={pagination}
      />

      <DeletePopup
        show={openDeleteScreenModal}
        onHide={() => {
          setOpenDeleteScreenModal(false);
          setSelectedId(null);
        }}
        content="Are you sure you want to delete this screen?"
        removeTitle="Cancel"
        onConfirm={handleConfirmDelete}
        disabled={isSubmitting}
        saveTitle={isSubmitting ? "Deleting..." : "Delete"}
      />

      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setStatusItem(null);
        }}
        title={statusItem?.is_active ? "Deactivate Screen" : "Activate Screen"}
        message={
          statusItem?.is_active
            ? "Are you sure you want to deactivate this screen?"
            : "Are you sure you want to activate this screen?"
        }
        onConfirm={handleConfirmStatusChange}
      />
    </>
  );
};

export default List;
